import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import StatusPill from "../components/StatusPill";
import { ArrowLeft, Edit2, Lock, Plus, Trash2 } from "lucide-react";
import PaymentDialog from "../components/PaymentDialog";
import LoanDialog from "../components/LoanDialog";
import HardDeleteDialog from "../components/HardDeleteDialog";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function LoanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [paymentDeleteTarget, setPaymentDeleteTarget] = useState(null);
  const [loanDeleteOpen, setLoanDeleteOpen] = useState(false);

  const load = () => api.get(`/loans/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, [id]);

  const closeLoan = async () => {
    await api.post(`/loans/${id}/close`);
    toast.success("Loan closed");
    load();
  };

  if (!data) return <div data-testid="loan-loading" className="p-4">Loading…</div>;
  const l = data.loan;
  const c = data.customer;
  const pct = l.financed_amount > 0 ? Math.min(100, (l.paid_amount / l.financed_amount) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="loan-detail-page">
      <Link to="/loans" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-loans">
        <ArrowLeft className="w-4 h-4" /> All loans
      </Link>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Loan</div>
            <h1 className="font-display text-3xl font-bold">{l.product_name}</h1>
            <div className="text-sm text-slate-600 mt-1">
              {c && <Link to={`/customers/${c.id}`} className="font-semibold text-emerald-700">{c.name}</Link>}
              {c && <span className="tabular"> · {c.mobile}</span>}
              {l.imei && <span className="ml-2 text-slate-500">IMEI: <span className="tabular">{l.imei}</span></span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <StatusPill status={l.status === "closed" ? "closed" : l.current_status} daysLate={l.days_late} />
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-loan-btn">
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
            {l.status !== "closed" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="close-loan-btn">
                    <Lock className="w-4 h-4 mr-1" /> Close loan
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Close this loan?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Marks the loan as closed. You can still view its history.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={closeLoan} data-testid="confirm-close-loan-btn">Close loan</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPayOpen(true)} data-testid="add-payment-btn" disabled={l.status === "closed"}>
              <Plus className="w-4 h-4 mr-1" /> Add Payment
            </Button>
            {isOwner && (
              <Button size="sm" variant="outline" onClick={() => setLoanDeleteOpen(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="delete-loan-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Sale price", value: fmtPKR(l.sale_price) },
            { label: "Down payment", value: fmtPKR(l.down_payment) },
            { label: "Financed", value: fmtPKR(l.financed_amount) },
            { label: "Per installment", value: fmtPKR(l.installment_value) },
            { label: "Duration", value: `${l.duration} × ${l.installment_type}` },
            { label: "Start date", value: l.start_date },
            { label: "Paid", value: fmtPKR(l.paid_amount) },
            { label: "Remaining", value: fmtPKR(l.remaining_amount) },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{s.label}</div>
              <div className="font-display font-bold text-lg tabular mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Progress</span><span className="tabular">{pct.toFixed(0)}%</span>
          </div>
          <Progress value={pct} className="h-2 [&>div]:bg-emerald-600" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-5 border-b border-slate-200 font-display font-semibold">Installment schedule</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">#</th>
                  <th className="text-left px-4 py-2 font-semibold">Due date</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {l.schedule.map((s, idx) => {
                  const paidUnits = l.installment_value > 0 ? Math.floor(l.paid_amount / l.installment_value) : 0;
                  const isPaid = idx < paidUnits;
                  const today = new Date().toISOString().slice(0, 10);
                  const status = isPaid ? "paid" : s.due_date < today ? "overdue" : s.due_date === today ? "due" : "upcoming";
                  return (
                    <tr key={s.n} className="border-t border-slate-100" data-testid={`sched-row-${s.n}`}>
                      <td className="px-4 py-2 tabular">{s.n}</td>
                      <td className="px-4 py-2 tabular">{s.due_date}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtPKR(s.amount)}</td>
                      <td className="px-4 py-2"><StatusPill status={status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-5 border-b border-slate-200 font-display font-semibold">Payment history</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-left px-4 py-2 font-semibold">Mode</th>
                  <th className="text-left px-4 py-2 font-semibold">Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.payments.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No payments yet.</td></tr>
                )}
                {data.payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`pay-row-${p.id}`}>
                    <td className="px-4 py-2 tabular">{p.date}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold text-emerald-700">{fmtPKR(p.amount)}</td>
                    <td className="px-4 py-2">{p.mode}</td>
                    <td className="px-4 py-2 text-slate-600">{p.note || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    {isOwner && (
                      <button onClick={() => setPaymentDeleteTarget(p)} className="text-slate-400 hover:text-red-600" data-testid={`del-pay-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} loanId={l.id} suggested={l.installment_value} onSaved={load} />
      <LoanDialog open={editOpen} onOpenChange={setEditOpen} initial={l} customerId={l.customer_id} onSaved={load} />

      <HardDeleteDialog
        open={!!paymentDeleteTarget}
        onOpenChange={(v) => !v && setPaymentDeleteTarget(null)}
        title="Payment"
        targetLabel={paymentDeleteTarget ? `PKR ${paymentDeleteTarget.amount} · ${paymentDeleteTarget.date}` : ""}
        endpoint={paymentDeleteTarget ? `/payments/${paymentDeleteTarget.id}` : ""}
        description="Yeh payment permanently delete ho jayegi. Loan ka remaining amount re-calculate ho jayega."
        onDeleted={() => { setPaymentDeleteTarget(null); load(); }}
      />

      <HardDeleteDialog
        open={loanDeleteOpen}
        onOpenChange={setLoanDeleteOpen}
        title="Loan"
        targetLabel={l.product_name}
        endpoint={`/loans/${l.id}`}
        previewEndpoint={`/loans/${l.id}/deletion-preview`}
        description="Loan ke agreements bhi delete ho jayenge. Agar koi bhi payment record exist hai, delete block ho jayega — pehle payments hatayein."
        onDeleted={() => { toast.success("Loan deleted."); navigate("/loans"); }}
      />
    </div>
  );
}
